/**
 * Wizard genera-preventivo-omologa (Blocco 6 STEP 4 sotto-commit 3, #107a/b).
 *
 * Sostituisce il vecchio GeneraPreventivoOmologaModal (form scheda omologa inline
 * + JSONB, incompatibile col BE refattorizzato). Due step:
 *   1. Preventivo  — tariffa, trasporto, validità, note, cliente porta materiale.
 *   2. Omologa     — selettore modalita_omologa:
 *        • "esistente": lista omologhe del produttore (scadute disabilitate);
 *        • "nuova": form omologa condiviso (OmologaFields), senza salvataggio —
 *          è il BE a creare l'omologa con la transizione.
 *
 * Una sola transizione finale (ricevuto|preventivo_omologa_generati →
 * preventivo_omologa_generati): in rigenerazione (#107b) il wizard è identico.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { X, ArrowLeft, ArrowRight, FileText, FilePlus, AlertTriangle, UserCheck, ExternalLink, Download } from 'lucide-react';
import { generaPreventivoOmologa, type CustomerDetail, type RichiestaDetail } from '../../api/richieste';
import { listOmologhe, apriSchedaPdfOmologa, apriFirmatoPdfOmologa } from '../../api/omologhe';
import type { Omologa } from '../../types/omologa';
import { omologaUpdateSchema, type OmologaUpdateData } from '../../schemas/omologa';
import i18n from '../../i18n';
import OmologaFields from '../OmologaForm';
import UsaDatiClienteForm from '../UsaDatiClienteForm';
import Badge from '../ui/Badge';

interface Props {
  richiestaId: string;
  produttoreId: string | null;
  customer: CustomerDetail;
  isRigenera?: boolean;
  tariffaAttuale: number | null;
  costoTrasportoAttuale: number | null;
  numeroTrasportiAttuale: number | null;
  validitaAttuale: number | null;
  notePreventivoAttuali: string | null;
  clientePortaMaterialeAttuale: boolean;
  /** orders.modalita_conferimento ('porto' | 'ritiro' | 'nd'): con 'porto' la
   *  PRIMA generazione preimposta "il cliente porta il materiale" (il cliente
   *  conferisce direttamente in impianto). In rigenerazione vale il dato
   *  persistito sull'ordine. */
  modalitaConferimento: string;
  // Direzione corrente sull'ordine + default di categoria (prompt 2/4) per il prefill.
  tipoOperazioneAttuale: string | null;
  categoriaTariffaDefault: number | null;
  categoriaTipoOperazioneDefault: string | null;
  omologaAssociataId?: string | null;
  onClose: () => void;
  onSaved: (updated: RichiestaDetail) => void;
  /** Materializzazione produttore dai dati cliente: aggiorna il parent senza
   *  chiudere il wizard (il nuovo produttore_id rientra via prop e sblocca). */
  onProduttoreMaterializzato: (updated: RichiestaDetail) => void;
}

const inputCls =
  'w-full text-sm border border-stone-border rounded px-3 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';
const labelCls = 'block text-xs font-medium text-ink-soft mb-1';

type Modalita = 'esistente' | 'nuova';

function statoBadge(o: Omologa) {
  if (o.stato === 'valida') return <Badge variant="success">{i18n.t('richieste.wizard.omologa_valida')}</Badge>;
  if (o.stato === 'scaduta') return <Badge variant="warning">{i18n.t('richieste.wizard.omologa_scaduta')}</Badge>;
  return <Badge variant="neutral">{i18n.t('richieste.wizard.omologa_indeterminata')}</Badge>;
}

export default function GeneraPreventivoOmologaWizard({
  richiestaId,
  produttoreId,
  customer,
  isRigenera = false,
  tariffaAttuale,
  costoTrasportoAttuale,
  numeroTrasportiAttuale,
  validitaAttuale,
  notePreventivoAttuali,
  clientePortaMaterialeAttuale,
  modalitaConferimento,
  tipoOperazioneAttuale,
  categoriaTariffaDefault,
  categoriaTipoOperazioneDefault,
  omologaAssociataId,
  onClose,
  onSaved,
  onProduttoreMaterializzato,
}: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2>(1);
  // Scorciatoia "Usa i dati del cliente" quando manca il produttore (ramo nuova).
  const [showUsaCliente, setShowUsaCliente] = useState(false);

  // ── Step 1: preventivo ──
  // Prezzo: tariffa già sull'ordine (rigenerazione) → altrimenti default categoria.
  const [tariffa, setTariffa] = useState(
    tariffaAttuale != null
      ? String(tariffaAttuale)
      : categoriaTariffaDefault != null
      ? String(categoriaTariffaDefault)
      : '',
  );
  // Direzione: se l'ordine ha già una direzione esplicita (≠ default colonna 'servizio')
  // usala; altrimenti precompila dal default di categoria.
  const [direzione, setDirezione] = useState<'servizio' | 'acquisto'>(
    tipoOperazioneAttuale && tipoOperazioneAttuale !== 'servizio'
      ? (tipoOperazioneAttuale as 'acquisto')
      : ((categoriaTipoOperazioneDefault as 'servizio' | 'acquisto') ?? 'servizio'),
  );
  // Conferimento "a porto" → alla prima generazione il flag nasce spuntato
  // (trasporto a carico del cliente). Resta modificabile; in rigenerazione
  // vince sempre il valore già persistito sull'ordine.
  const clientePortaIniziale =
    clientePortaMaterialeAttuale || (!isRigenera && modalitaConferimento === 'porto');
  const [clientePorta, setClientePorta] = useState(clientePortaIniziale);
  // Con flag iniziale attivo il trasporto nasce vuoto (come dopo un check
  // manuale): se l'utente lo deseleziona, il campo riappare vuoto/editabile.
  const [trasporto, setTrasporto] = useState(
    !clientePortaIniziale && costoTrasportoAttuale != null ? String(costoTrasportoAttuale) : '',
  );
  // N. viaggi/trasporti stimati: come il costo trasporto, vuoto se il cliente
  // porta il materiale (nessun trasporto Ellezeta).
  const [numeroViaggi, setNumeroViaggi] = useState(
    !clientePortaIniziale && numeroTrasportiAttuale != null ? String(numeroTrasportiAttuale) : '',
  );
  const [validita, setValidita] = useState(String(validitaAttuale ?? 30));
  const [notePrev, setNotePrev] = useState(notePreventivoAttuali ?? '');

  // ── Step 2: omologa ──
  const [modalita, setModalita] = useState<Modalita>('esistente');
  const [omologhe, setOmologhe] = useState<Omologa[]>([]);
  const [loadingOmol, setLoadingOmol] = useState(false);
  const [selectedOmologaId, setSelectedOmologaId] = useState<string | null>(omologaAssociataId ?? null);
  const [pdfErr, setPdfErr] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function openOmologaPdf(kind: 'scheda' | 'firmato', omologaId: string) {
    setPdfErr(null);
    try {
      if (kind === 'scheda') await apriSchedaPdfOmologa(omologaId);
      else await apriFirmatoPdfOmologa(omologaId);
    } catch {
      setPdfErr(
        kind === 'scheda'
          ? t('richieste.wizard.errore_apri_scheda')
          : t('richieste.wizard.errore_apri_firmato'),
      );
    }
  }

  // Form omologa condiviso (ramo "nuova") — RHF nel wizard, OmologaFields lo rende.
  // Schema senza `produttore_id`: il produttore non è un campo del form (lo deriva
  // il BE da orders.produttore_id). Usare omologaCreateSchema qui rendeva
  // `produttore_id` un campo required invisibile che bloccava il submit con un
  // catch-all quando l'ordine non aveva produttore.
  const {
    register, control, setValue, getValues, trigger,
    formState: { errors },
  } = useForm<OmologaUpdateData>({
    resolver: zodResolver(omologaUpdateSchema),
    defaultValues: {
      numero_conferimenti: null, frequenza: '', pezzatura: '',
      note: '', data_firma: '', data_scadenza: '',
      trasportatori_ids: [], intermediari_ids: [],
    },
  });

  useEffect(() => {
    if (step !== 2 || modalita !== 'esistente' || !produttoreId) return;
    void (async () => {
      setLoadingOmol(true);
      try {
        const res = await listOmologhe({ produttore_id: produttoreId, limit: 100 });
        setOmologhe(res.items);
      } catch {
        setError(t('richieste.wizard.errore_omologhe'));
      } finally {
        setLoadingOmol(false);
      }
    })();
  }, [step, modalita, produttoreId, t]);

  function validateStep1(): boolean {
    const tariffaNum = parseFloat(tariffa.replace(',', '.'));
    if (isNaN(tariffaNum) || tariffaNum < 0) {
      setError(t('richieste.wizard.val_tariffa'));
      return false;
    }
    const v = parseInt(validita, 10);
    if (isNaN(v) || v < 1 || v > 365) {
      setError(t('richieste.wizard.val_validita'));
      return false;
    }
    if (!clientePorta && !trasporto.trim()) {
      setError(t('richieste.wizard.val_trasporto'));
      return false;
    }
    setError(null);
    return true;
  }

  function goStep2() {
    if (validateStep1()) setStep(2);
  }

  async function handleSubmit() {
    setError(null);
    if (!validateStep1()) { setStep(1); return; }

    const preventivo = {
      tariffa_unitaria_eur_kg: parseFloat(tariffa.replace(',', '.')),
      costo_trasporto_eur_per_viaggio: clientePorta
        ? null
        : trasporto
        ? parseFloat(trasporto.replace(',', '.'))
        : null,
      numero_trasporti_stimati: clientePorta
        ? null
        : numeroViaggi.trim()
        ? parseInt(numeroViaggi, 10)
        : null,
      validita_preventivo_giorni: parseInt(validita, 10),
      note_preventivo: notePrev.trim() || null,
      cliente_porta_materiale: clientePorta,
      tipo_operazione: direzione,
    };

    let omologaPart;
    if (modalita === 'esistente') {
      if (!selectedOmologaId) {
        setError(t('richieste.wizard.val_seleziona_omologa'));
        return;
      }
      omologaPart = { modalita_omologa: 'esistente' as const, omologa_id: selectedOmologaId };
    } else {
      if (!produttoreId) {
        setError(t('richieste.wizard.val_no_produttore'));
        return;
      }
      const ok = await trigger();
      if (!ok) {
        setError(t('richieste.wizard.val_campi_omologa'));
        return;
      }
      const d = getValues();
      omologaPart = {
        modalita_omologa: 'nuova' as const,
        omologa_nuova: {
          numero_conferimenti: d.numero_conferimenti != null ? Number(d.numero_conferimenti) : null,
          frequenza: d.frequenza?.trim() || null,
          pezzatura: d.pezzatura?.trim() || null,
          note: d.note?.trim() || null,
          data_firma: d.data_firma?.trim() || null,
          data_scadenza: d.data_scadenza?.trim() || null,
          trasportatori_ids: d.trasportatori_ids ?? [],
          intermediari_ids: d.intermediari_ids ?? [],
        },
      };
    }

    setLoading(true);
    try {
      const updated = await generaPreventivoOmologa(richiestaId, { ...preventivo, ...omologaPart });
      onSaved(updated);
    } catch (err: unknown) {
      const resp = (err as { response?: { data?: { detail?: string } } })?.response;
      const detail = resp?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('richieste.wizard.errore_salvataggio'));
    } finally {
      setLoading(false);
    }
  }

  const titolo = isRigenera ? t('richieste.wizard.titolo_rigenera') : t('richieste.wizard.titolo_genera');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-ink-dark">{titolo}</h2>
            <p className="text-xs text-ink-soft mt-0.5">
              {t('richieste.wizard.step_label', { step, nome: step === 1 ? t('richieste.wizard.step_preventivo') : t('richieste.wizard.step_omologa') })}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto">
          {isRigenera && step === 1 && (
            <div className="px-3 py-2 bg-accent-info-bg border border-[#BAE6FD] rounded text-xs text-accent-info">
              {t('richieste.wizard.rigenerazione_avviso')}
            </div>
          )}

          {step === 1 && (
            <section className="space-y-3">
              <div className="rounded-lg border border-stone-mid bg-stone-bg/50 px-4 py-3">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="preventivo-cliente-porta"
                    checked={clientePorta}
                    onChange={(e) => {
                      setClientePorta(e.target.checked);
                      if (e.target.checked) {
                        setTrasporto('');
                        setNumeroViaggi('');
                      }
                    }}
                    className="w-4 h-4 accent-sage cursor-pointer"
                  />
                  <span className="text-sm font-medium text-ink-dark">
                    {t('richieste.wizard.cliente_porta')}
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>
                    {t('richieste.wizard.tariffa_unitaria')} <span className="text-accent-danger">*</span>
                  </label>
                  <input className={inputCls} type="number" min="0" step="0.01"
                    value={tariffa} onChange={(e) => setTariffa(e.target.value)} placeholder="es. 150.00" />
                </div>
                {!clientePorta && (
                  <div>
                    <label className={labelCls}>
                      {t('richieste.wizard.costo_trasporto')} <span className="text-accent-danger">*</span>
                    </label>
                    <input className={inputCls} type="number" min="0" step="0.01"
                      data-testid="preventivo-costo-trasporto"
                      value={trasporto} onChange={(e) => setTrasporto(e.target.value)} placeholder="es. 200.00" />
                  </div>
                )}
                {!clientePorta && (
                  <div>
                    <label className={labelCls}>
                      {t('richieste.wizard.numero_trasporti')}
                    </label>
                    <input className={inputCls} type="number" min="0" step="1"
                      data-testid="preventivo-numero-trasporti"
                      value={numeroViaggi} onChange={(e) => setNumeroViaggi(e.target.value)} placeholder="es. 3" />
                    <p className="text-[11px] text-ink-faint mt-0.5">
                      {t('richieste.wizard.numero_trasporti_hint')}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className={labelCls}>
                  {t('richieste.wizard.direzione')} <span className="text-accent-danger">*</span>
                </label>
                <select className={inputCls} data-testid="preventivo-direzione"
                  value={direzione} onChange={(e) => setDirezione(e.target.value as 'servizio' | 'acquisto')}>
                  <option value="servizio">{t('richieste.wizard.direzione_servizio')}</option>
                  <option value="acquisto">{t('richieste.wizard.direzione_acquisto')}</option>
                </select>
                <p className="text-[11px] text-ink-faint mt-0.5">
                  {t('richieste.wizard.direzione_hint')}
                </p>
              </div>

              <div>
                <label className={labelCls}>{t('richieste.wizard.validita')}</label>
                <input className={inputCls} type="number" min="1" max="365"
                  value={validita} onChange={(e) => setValidita(e.target.value)} />
              </div>

              <div>
                <label className={labelCls}>{t('richieste.wizard.note_preventivo')}</label>
                <textarea className={inputCls} rows={2} value={notePrev} onChange={(e) => setNotePrev(e.target.value)} />
              </div>
            </section>
          )}

          {step === 2 && (
            <section className="space-y-4">
              {/* Selettore modalità */}
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setModalita('esistente')}
                  className={`flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg border transition-colors ${
                    modalita === 'esistente'
                      ? 'border-sage bg-sage-light text-sage-dark font-medium'
                      : 'border-stone-border text-ink-mid hover:bg-stone-bg'
                  }`}>
                  <FileText size={15} /> {t('richieste.wizard.usa_esistente')}
                </button>
                <button type="button" onClick={() => setModalita('nuova')}
                  className={`flex items-center gap-2 px-3 py-2.5 text-sm rounded-lg border transition-colors ${
                    modalita === 'nuova'
                      ? 'border-sage bg-sage-light text-sage-dark font-medium'
                      : 'border-stone-border text-ink-mid hover:bg-stone-bg'
                  }`}>
                  <FilePlus size={15} /> {t('richieste.wizard.crea_nuova')}
                </button>
              </div>

              {modalita === 'esistente' && (
                <div className="space-y-2">
                  {!produttoreId && (
                    <p className="text-sm text-accent-danger">{t('richieste.wizard.no_produttore')}</p>
                  )}
                  {loadingOmol && <p className="text-sm text-ink-soft">{t('richieste.wizard.caricamento_omologhe')}</p>}
                  {!loadingOmol && produttoreId && omologhe.length === 0 && (
                    <p className="text-sm text-ink-soft italic">
                      {t('richieste.wizard.nessuna_omologa')}
                    </p>
                  )}
                  {omologhe.map((o) => {
                    const scaduta = o.stato === 'scaduta';
                    const selected = selectedOmologaId === o.id;
                    return (
                      <button
                        key={o.id}
                        type="button"
                        disabled={scaduta}
                        title={scaduta ? t('richieste.wizard.scaduta_title') : undefined}
                        onClick={() => setSelectedOmologaId(o.id)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors flex items-center justify-between gap-3 ${
                          scaduta
                            ? 'border-stone-mid bg-stone-bg/40 opacity-60 cursor-not-allowed'
                            : selected
                            ? 'border-sage bg-sage-light'
                            : 'border-stone-border hover:bg-stone-bg'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-ink-dark truncate">
                            {o.numero_omologa || t('richieste.wizard.omologa_fallback', { id: o.id.slice(0, 8) })}
                          </p>
                          <p className="text-xs text-ink-soft">
                            {o.data_scadenza ? t('richieste.wizard.scadenza', { data: o.data_scadenza }) : t('richieste.wizard.scadenza_non_impostata')}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {statoBadge(o)}
                          <Badge variant="neutral">{o.stato_firma}</Badge>
                        </div>
                      </button>
                    );
                  })}
                  {omologhe.some((o) => o.stato === 'scaduta') && (
                    <p className="flex items-center gap-1.5 text-xs text-ink-soft">
                      <AlertTriangle size={12} /> {t('richieste.wizard.scadute_non_selezionabili')}
                    </p>
                  )}

                  {/* Documenti dell'omologa selezionata (sola lettura): scheda +
                      firmato via gli endpoint omologa (/scheda-pdf, /firmato-pdf). */}
                  {(() => {
                    const sel = omologhe.find((o) => o.id === selectedOmologaId);
                    if (!sel) return null;
                    const hasScheda = !!sel.pdf_scheda_url;
                    const hasFirmato = sel.stato_firma === 'firmata' && !!sel.pdf_firmato_url;
                    return (
                      <div className="mt-1 border border-stone-mid rounded-lg p-3 space-y-2">
                        <p className="text-xs font-medium text-ink-soft">{t('richieste.wizard.documenti_omologa')}</p>
                        {hasScheda && (
                          <button type="button" onClick={() => void openOmologaPdf('scheda', sel.id)}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors">
                            <ExternalLink size={14} /> {t('richieste.wizard.anteprima_scheda')}
                          </button>
                        )}
                        {hasFirmato && (
                          <button type="button" onClick={() => void openOmologaPdf('firmato', sel.id)}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors">
                            <Download size={14} /> {t('richieste.wizard.apri_firmato')}
                          </button>
                        )}
                        {!hasScheda && !hasFirmato && (
                          <p className="text-xs text-ink-soft italic">
                            {t('richieste.wizard.nessun_pdf', { stato: sel.stato_firma })}
                          </p>
                        )}
                        {pdfErr && <p className="text-xs text-accent-danger">{pdfErr}</p>}
                      </div>
                    );
                  })()}
                </div>
              )}

              {modalita === 'nuova' && !produttoreId && (
                <div className="space-y-3">
                  <p className="text-sm text-accent-danger">
                    {t('richieste.wizard.val_no_produttore')}
                  </p>
                  {!showUsaCliente ? (
                    <button type="button" onClick={() => setShowUsaCliente(true)}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-sage border border-sage rounded-lg hover:bg-sage-light transition-colors">
                      <UserCheck size={15} /> {t('richieste.wizard.usa_dati_cliente')}
                    </button>
                  ) : (
                    <div className="border border-stone-mid rounded-lg p-4">
                      <UsaDatiClienteForm
                        richiestaId={richiestaId}
                        customer={customer}
                        submitLabel={t('richieste.wizard.crea_produttore_prosegui')}
                        onMaterialized={(updated) => {
                          setShowUsaCliente(false);
                          onProduttoreMaterializzato(updated);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {modalita === 'nuova' && produttoreId && (
                <div className="border border-stone-mid rounded-lg p-4">
                  <OmologaFields
                    register={register}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    getValues={getValues}
                  />
                </div>
              )}
            </section>
          )}

          {error && (
            <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg flex-shrink-0">
          <div>
            {step === 2 && (
              <button type="button" onClick={() => setStep(1)} disabled={loading}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
                <ArrowLeft size={14} /> {t('richieste.wizard.indietro')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={onClose} disabled={loading}
              className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
              {t('common.azioni.annulla')}
            </button>
            {step === 1 ? (
              <button type="button" onClick={goStep2}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors">
                {t('richieste.wizard.avanti')} <ArrowRight size={14} />
              </button>
            ) : (
              <button type="button" onClick={handleSubmit}
                disabled={loading || (modalita === 'nuova' && !produttoreId)}
                className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60">
                {loading ? t('richieste.wizard.generazione') : isRigenera ? t('richieste.wizard.btn_rigenera_breve') : t('richieste.wizard.btn_genera_breve')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
